import { getSharedStyleSheet } from './sharedStyleSheet.js';

class TextareaButton extends HTMLElement {
    static observedAttributes = ['textarea', 'button', 'buttontitle', 'onbuttonclick', 'value', 'autoselect', 'disabled', 'title'];

    constructor() {
        super();
        this._onbuttonclick = null;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open'});

            this.shadowRoot.host.style.display = 'block';

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.style.display = 'flex';
            this._wrapper.style.width = '100%';
            this._wrapper.style.height = '100%';

            this._textarea = document.createElement('textarea');
            this._textarea.style.margin = '0';
            this._textarea.style.flexGrow = '1';
            this._textarea.style.resize = 'none';
            this._textarea.style.borderRadius = '.3rem 0 0 .3rem';
            this._textarea.style.width = '100%';
            this._textarea.style.minWidth = '40px';
            this._textarea.style.minHeight = '1em';

            this._wrapper.appendChild(this._textarea);

            this._button = document.createElement('button');
            this._button.style.borderRadius = '0 .3rem .3rem 0';
            this._button.style.borderLeft = 'none';
            this._button.style.marginLeft = '0';
            this._button.style.padding = '0 .5rem';

            const tooltip = this.getAttribute("title");

            if (tooltip) {
                this._button.title = tooltip;
                this._button.ariaLabel = tooltip;
            }

            // Add hover style
            this._button.addEventListener('mouseenter', (e) => {
                if (!this.getAttribute('disabled')) {
                    this._button.style.borderColor = 'var(--input-border-color-hover) var(--input-border-color-hover) var(--input-border-color-hover) transparent';
                    this._textarea.style.borderRightColor = 'var(--input-border-color-hover)';
                }
            });

            this._button.addEventListener('mouseleave', (e) => {
                if (!this.getAttribute('disabled')) {
                    this._button.style.borderColor = 'var(--input-border-color) var(--input-border-color) var(--input-border-color) transparent';
                    this._textarea.style.borderRightColor = 'var(--input-border-color)';
                }
            });

            if (!!this.autoselect) {
                this._textarea.addEventListener('focus', (e) => {
                    this._textarea.select();
                });
            }

            this._wrapper.appendChild(this._button);
            this.shadowRoot.appendChild(this._wrapper);

            this._button.addEventListener('click', (e) => {
                const handler = this.onbuttonclick;
                if (handler) handler(e);
            });

            this._textarea.addEventListener('keyup', (e) => {
                this.setAttribute('value', this._textarea.value);
            });

            this._textarea.addEventListener('change', (e) => {
                this.shadowRoot.dispatchEvent(new CustomEvent('change', {
                    composed: true,
                    bubbles: true
                }));
            });

            // Redraw on resize
            window.addEventListener('resize', (e) => {
                this.dynamicRender();
            });
        }

        this.dynamicRender();
    }

    dynamicRender() {
        if (!this._textarea || !this._button) return;

        this._textarea.value = this.value;
        this._button.textContent = this.buttontitle;

        this._textarea.readOnly = !!this.getAttribute('readonly');

        if (!!this.getAttribute('disabled')) {
            this._textarea.disabled = true;
            this._button.disabled = true;

            this._textarea.style.borderColor = 'var(--input-border-color-disabled)';
            this._textarea.style.color = 'var(--input-text-color-disabled)';
            this._textarea.style.borderRightColor = 'var(--input-border-color-disabled)';

            this._button.style.borderColor = 'var(--input-border-color-disabled) var(--input-border-color-disabled) var(--input-border-color-disabled) transparent';
            this._button.style.color = 'var(--input-text-color-disabled)';

        } else {
            this._textarea.disabled = false;
            this._button.disabled = false;

            this._textarea.style.borderColor = 'var(--input-border-color)';
            this._textarea.style.backgroundColor = 'var(--input-bg-color)';
            this._textarea.style.color = 'var(--input-text-color)';
            this._textarea.style.borderRightColor = 'var(--input-border-color)';

            this._button.style.borderColor = 'var(--input-border-color) var(--input-border-color) var(--input-border-color) transparent';
            this._button.style.backgroundColor = 'var(--page-bg-color)';
            this._button.style.color = 'var(--page-text-color)';
        }

        // Auto-size height according to text content
        let scrollHeight = this._textarea.scrollHeight;
        if (scrollHeight > 200) scrollHeight = 200;
        if (scrollHeight < this._wrapper.clientHeight || !!this.getAttribute("no-auto-height")) {
            this._textarea.style.height = '100%';
        } else if (scrollHeight > 10) {
            this._textarea.style.height = scrollHeight + 'px';
        }
    }

    get textarea() {
        return this._textarea;
    }

    get button() {
        return this._button;
    }

    get buttontitle() {
        return this.getAttribute('buttontitle');
    }

    set buttontitle(_title) {
        this.setAttribute("buttontitle", _title);
        this.dynamicRender();
    }

    get value() {
        return this.getAttribute('value');
    }

    set value(_val) {
        this.setAttribute("value", _val);
        this.dynamicRender();
    }

    get autoselect() {
        return this.getAttribute('autoselect');
    }

    set autoselect(_autoselect) {
        this.setAttribute("autoselect", _autoselect);
    }

    get onbuttonclick() {
        return this._onbuttonclick;
    }

    set onbuttonclick(_handler) {
        this._onbuttonclick = _handler;
    }
}

class TextareaCopyButton extends TextareaButton {
    constructor() {
        super();

        this._onbuttonclick = (e) => {
            this._textarea.select();
            document.execCommand("copy");

            this._button.style.width = '3em';

            // Show tick icon
            this._button.innerHTML = '&#10003;'

            window.setTimeout(() => {
                this.button.textContent = this.buttontitle;
                this.button.style.width = '3em';
            }, 2000);
       };
    }

    connectedCallback() {
        super.connectedCallback();

        this._button.style.width = '3em';
        this.buttontitle = "⧉";

        const tooltip = this.getAttribute("title");

        if (!tooltip) {
            this.setAttribute("title", "Copy");
        }
    }
}

customElements.define('textarea-button', TextareaButton);
customElements.define('textarea-copy-button', TextareaCopyButton);
